import * as React from 'react';
import { get, isEmpty } from 'lodash';
import { compose, withHandlers, withProps, withState } from 'recompose';

import ConfirmButton from '@inqool/uas-web-core/es/components/controls/ConfirmButton';
import { ModalButton } from '@components/modalButton/ModalButton';
import { OperationalWarehouseTransferForm } from './operationalWarehouseTransferForm';
import { OperationalWarehouseItemsForm } from './operationalWarehouseItemsForm';
import { itemStateEnum } from './_operationalWarehouseEnums';
import { withNotifications } from '@components/hoc/withNotifications';
import { deleteItems } from './_operationalWarehouseActions';
import { Loader } from '@components/loader';
import { hasAnyPermission } from '@utils/utils';
import { ColumnManager } from '@modules/columnSettings/ColumnManager';
import { Agenda } from '@modules/columnSettings/_columnSettingsEnums';

const enhance = compose<any, any>(
  withNotifications({
    deleteItems
  }),
  withState('loader', 'setLoader', false),
  withHandlers({
    handleDeleteItems: ({ deleteItemsNotify, checkedItems, refresh, setLoader }) => async () => {
      setLoader(true);
      const ok = await deleteItemsNotify(checkedItems.map(({ id }) => id));
      setLoader(false);

      if (ok) {
        refresh();
      }
    }
  })
);

const View = ({ refresh, checkedItems, handleDeleteItems, loader }) => {
  return (
    <div style={{ display: 'flex' }}>
      {loader && <Loader />}
      <ColumnManager agenda={Agenda.OPERATIONAL_WAREHOUSE} />
      {[
        {
          label: 'Generování předmětů',
          ModalComponent: OperationalWarehouseItemsForm,
          formProps: {
            url: 'generate',
            successMessage: 'Předměty byly vygenerovány',
            errorMessage: 'Nepodařilo se vygenerovat předměty'
          }
        },
        {
          label: 'Vydání předmětů strážníkovi',
          formProps: {
            url: 'issue',
            successMessage: 'Předměty byly vydány strážníkovi',
            errorMessage: 'Nepodařilo se vydat předměty strážníkovi',
            filterField: 'warehouse.id',
            firstField: {
              name: 'fromWarehouseId',
              label: 'Sklad',
              url: '/metropolis/api/owa/warehouse/parametrized',
              doPost: true,
              labelFunction: o => get(o, 'name')
            },
            secondField: {
              name: 'toEmployeeId',
              label: 'Strážník',
              url: '/metropolis/api/hr/employees',
              labelFunction: o => get(o, 'fullName')
            },
            filters: [{ field: 'state.id', operation: 'EQ', value: 'IN_WAREHOUSE' }]
          }
        },
        {
          label: 'Přesun mezi strážníky',
          formProps: {
            url: 'transfer',
            successMessage: 'Předměty byly přesunuty na strážníka',
            errorMessage: 'Nepodařilo se přesunout předměty na strážníka',
            filterField: 'employee.id',
            firstField: {
              name: 'fromEmployeeId',
              label: 'Strážník, který vydává předměty',
              url: '/metropolis/api/hr/employees',
              labelFunction: o => get(o, 'fullName')
            },
            secondField: {
              name: 'toEmployeeId',
              label: 'Strážník, který přijímá předměty',
              url: '/metropolis/api/hr/employees',
              labelFunction: o => get(o, 'fullName')
            },
            filters: [{ field: 'state.id', operation: 'EQ', value: 'ISSUED' }]
          }
        },
        {
          label: 'Přesun mezi sklady',
          formProps: {
            url: 'transfer-warehouse',
            successMessage: 'Předměty byly přesunuty na sklad',
            errorMessage: 'Nepodařilo se přesunout předměty na sklad',
            filterField: 'warehouse.id',
            firstField: {
              name: 'fromWarehouseId',
              label: 'Sklad, odkud se přesunou předměty',
              url: '/metropolis/api/owa/warehouse/parametrized',
              doPost: true,
              labelFunction: o => get(o, 'name')
            },
            secondField: {
              name: 'toWarehouseId',
              label: 'Sklad, do kterého se přesunou předměty',
              url: '/metropolis/api/owa/warehouse/parametrized',
              doPost: true,
              labelFunction: o => get(o, 'name')
            },
            filters: [{ field: 'state.id', operation: 'EQ', value: 'IN_WAREHOUSE' }]
          }
        },
        {
          label: 'Vrácení na sklad',
          formProps: {
            url: 'return',
            successMessage: 'Předměty byly vráceny na sklad',
            errorMessage: 'Nepodařilo se vrátit předměty na sklad',
            filterField: 'employee.id',
            firstField: {
              name: 'returnFromEmployeeId',
              label: 'Strážník',
              url: '/metropolis/api/hr/employees',
              labelFunction: o => get(o, 'fullName')
            },
            secondField: {
              name: 'returnToWarehouseId',
              label: 'Sklad',
              url: '/metropolis/api/owa/warehouse/parametrized',
              doPost: true,
              labelFunction: o => get(o, 'name')
            },
            filters: [{ field: 'state.id', operation: 'EQ', value: 'ISSUED' }]
          }
        },
        {
          label: 'Odebrání ze skladu',
          formProps: {
            url: 'remove',
            successMessage: 'Předmět byl odebrán ze skladu',
            errorMessage: 'Nepodařilo se odebrat předmět ze skladu',
            filterField: 'warehouse.id',
            oneItem: true,
            fileUpload: true,
            firstField: {
              name: 'warehouse',
              label: 'Sklad',
              url: '/metropolis/api/owa/warehouse/parametrized',
              doPost: true,
              labelFunction: o => get(o, 'name')
            },
            secondField: {
              local: true,
              name: 'finalState',
              label: 'Stav',
              items: itemStateEnum.filter(({ id }) => ['LOST', 'STOLEN', 'DESTROYED', 'UNDER_REPAIR'].indexOf(id) >= 0)
            },
            filters: [{ field: 'state.id', operation: 'EQ', value: 'IN_WAREHOUSE' }]
          }
        },
        {
          label: 'Vrácení předmětů',
          formProps: {
            url: 'return-repaired',
            successMessage: 'Předměty byly vráceny',
            errorMessage: 'Nepodařilo se vrátit předměty',
            filters: [
              {
                operation: 'OR',
                filter: [
                  { field: 'state.id', operation: 'EQ', value: 'LOST' },
                  { field: 'state.id', operation: 'EQ', value: 'STOLEN' },
                  { field: 'state.id', operation: 'EQ', value: 'DESTROYED' },
                  { field: 'state.id', operation: 'EQ', value: 'UNDER_REPAIR' }
                ]
              }
            ]
          }
        }
      ].map(({ label, formProps = {}, ModalComponent = OperationalWarehouseTransferForm }) => (
        <ModalButton
          key={label}
          label={label}
          ModalComponent={withProps({ ...formProps, label, refresh })(ModalComponent)}
          style={{ marginRight: 5 }}
          dialogStyles={{
            contentStyle: {
              paddingTop: 10,
              transform: 'none',
              width: '90vw',
              minWidth: '90vw'
            },
            bodyStyle: { padding: '0', maxWidth: '95vw', minHeight: '98vh' },
            style: { paddingTop: 0 }
          }}
        />
      ))}
      {hasAnyPermission(['ROLE_OPERATIONAL_WAREHOUSE_DELETE']) && (
        <ConfirmButton
          label="Zlikvidovat"
          onClick={handleDeleteItems}
          disabled={isEmpty(checkedItems)}
          style={{ marginRight: 5 }}
        />
      )}
    </div>
  );
};

export const ActionBar = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/operationalWarehouse/operationalWarehouseGridButtons.tsx